/**
 * Toast Notification Component
 * Simple, lightweight toast notifications using Tailwind CSS
 */

import { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, X, Loader2 } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'loading';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  action?: ToastAction;
}

interface ToastProps {
  toast: ToastMessage;
  onClose: (id: string) => void;
}

/**
 * Individual toast notification with auto-dismiss
 */
function Toast({ toast, onClose }: ToastProps) {
  useEffect(() => {
    // Don't auto-dismiss loading toasts or toasts with custom duration of 0
    if (toast.type === 'loading' || toast.duration === 0) {
      return;
    }

    const duration = toast.duration ?? 5000;
    const timer = setTimeout(() => {
      onClose(toast.id);
    }, duration);

    return () => clearTimeout(timer);
  }, [toast.id, toast.type, toast.duration, onClose]);

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return CheckCircle2;
      case 'error':
        return XCircle;
      case 'warning':
        return AlertTriangle;
      case 'loading':
        return Loader2;
    }
  };

  const getColors = () => {
    switch (toast.type) {
      case 'success':
        return {
          bg: 'bg-emerald-900/90',
          border: 'border-emerald-700',
          icon: 'text-emerald-400',
        };
      case 'error':
        return {
          bg: 'bg-red-900/90',
          border: 'border-red-700',
          icon: 'text-red-400',
        };
      case 'warning':
        return {
          bg: 'bg-amber-900/90',
          border: 'border-amber-700',
          icon: 'text-amber-400',
        };
      case 'loading':
        return {
          bg: 'bg-blue-900/90',
          border: 'border-blue-700',
          icon: 'text-blue-400',
        };
    }
  };

  const Icon = getIcon();
  const colors = getColors();
  const isLoading = toast.type === 'loading';

  return (
    <div
      className={`${colors.bg} ${colors.border} border backdrop-blur-sm rounded-lg shadow-lg p-4 flex items-start gap-3 min-w-[320px] max-w-md animate-slide-in`}
      role="alert"
    >
      <Icon className={`w-5 h-5 ${colors.icon} flex-shrink-0 mt-0.5 ${isLoading ? 'animate-spin' : ''}`} />
      <div className="flex-1">
        <p className="text-sm text-zinc-100">{toast.message}</p>
        {toast.action && (
          <button
            onClick={() => {
              toast.action?.onClick();
              onClose(toast.id);
            }}
            className="mt-2 text-xs font-medium text-blue-400 hover:text-blue-300 transition-colors"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      {!isLoading && (
        <button
          onClick={() => onClose(toast.id)}
          className="text-zinc-400 hover:text-zinc-100 transition-colors flex-shrink-0"
          aria-label="Close notification"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

/**
 * Toast container component - manages multiple toasts
 */
export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  // Expose global toast function
  useEffect(() => {
    window.showToast = (type: ToastType, message: string, options?: Partial<ToastMessage>) => {
      const id = options?.id ?? `toast-${Date.now()}-${Math.random()}`;
      // A caller-supplied id replaces the existing toast instead of stacking
      setToasts((prev) => [...prev.filter((t) => t.id !== id), { ...options, id, type, message }]);
      return id;
    };

    window.dismissToast = (id: string) => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    };

    return () => {
      delete window.showToast;
      delete window.dismissToast;
    };
  }, []);

  const handleClose = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onClose={handleClose} />
      ))}
    </div>
  );
}

/**
 * Toast helper functions
 */
export const toast = {
  success: (message: string, options?: Partial<ToastMessage>): string | undefined => {
    if (window.showToast) {
      return window.showToast('success', message, options);
    }
    return undefined;
  },
  error: (message: string, options?: Partial<ToastMessage>): string | undefined => {
    if (window.showToast) {
      return window.showToast('error', message, options);
    }
    return undefined;
  },
  warning: (message: string, options?: Partial<ToastMessage>): string | undefined => {
    if (window.showToast) {
      return window.showToast('warning', message, options);
    }
    return undefined;
  },
  loading: (message: string): string | undefined => {
    if (window.showToast) {
      return window.showToast('loading', message);
    }
    return undefined;
  },
  dismiss: (id?: string): void => {
    if (id && window.dismissToast) {
      window.dismissToast(id);
    }
  },
};

/**
 * Global type declarations
 */
declare global {
  interface Window {
    showToast?: (type: ToastType, message: string, options?: Partial<ToastMessage>) => string | undefined;
    dismissToast?: (id: string) => void;
  }
}
